import { asReadonlyBS, createSetterBS } from '@ocodelib/ex-util/rx';
import type { IframeController } from '@ocodelib/iframe-parent';
import { Closables, errmsg } from '@ocodelib/util/misc';
import type {
  IProjectLoader,
  ProjectFile,
  ProjectKey,
  ProjectLoadResult,
} from '@repo/project-interface';
import { DefaultProjectSerializer } from '@repo/project-serializer/impl';
import { toast } from 'react-toastify';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import sanitizeFilename from 'sanitize-filename';
import type { IframeMonacoEditorType } from '../iframes';
import { NewProjectLoader } from '../project-loaders';
import type { PyBasicAppType } from '../types';

interface Options {
  ctx: { canceled: boolean };
  previewMode: boolean;
  newProjectLoader?: IProjectLoader<ProjectFile[]>;
  initialProjectLoader?: IProjectLoader<ProjectFile[]>;
}

export async function createPyBasicApp(options: Options, callback?: (app: PyBasicAppType) => void) {
  const {
    ctx,
    previewMode,
    newProjectLoader = new NewProjectLoader(),
    initialProjectLoader,
  } = options;
  const closables = new Closables();

  // iframe 요소
  const execIframe$ = new BehaviorSubject<IframeController | null>(null);

  // 파일 목록
  const files$ = new BehaviorSubject<ProjectFile[]>([]);

  // 프로젝트 이름
  const projectName$ = new BehaviorSubject<string>('새 프로젝트');

  // 프로젝트 키
  const projectKey$ = new BehaviorSubject<ProjectKey | null>(null);

  // 선택된 파일
  const selectedFile$ = new BehaviorSubject<ProjectFile | null>(null);

  // 모나코 모델이 변경되었는지 여부
  // 파일의 내용이 변경되거나 파일명이 변경되는 경우 값이 발행된다.
  const fileChangedTimestamp$ = new BehaviorSubject(0);

  const projectLoading$ = new BehaviorSubject(true);

  // 에디터 인스턴스
  const editor$ = new BehaviorSubject<IframeMonacoEditorType | null>(null);

  // 자동실행 여부
  const autoRefreshEnabled$ = new BehaviorSubject(true);

  // IFRAME 에디터 생성
  const setupCodeEditor = (editor: IframeMonacoEditorType | undefined) => {
    destroyCodeEditor(); // destroy previous editor
    if (!editor) {
      editor$.next(null);
      return;
    }

    editor.setOptions({
      language: 'python',
      options: {
        theme: 'github-light',
        tabSize: 2,
        insertSpaces: true,
        fontSize: 16,
        automaticLayout: true,
        'semanticHighlighting.enabled': true,
        minimap: {
          enabled: false,
        },
      },
    });
    editor$.next(editor ?? null);
  };

  // 모나코 에디터 파괴
  const destroyCodeEditor = () => {
    const editor = editor$.value;
    if (editor) {
      editor.close();
      editor$.next(null);
    }
  };

  // 콘솔 로그 지우기
  const clearLogs = () => {
    const iframe = execIframe$.value;
    if (!iframe) return;
    iframe.runCmd('clear');
  };

  async function serializeToPjtFile(): Promise<File | null> {
    const files = files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return null;
    }

    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });

    const projectKey = projectKey$.value;
    return await DefaultProjectSerializer.serializePjtFile(
      {
        breed: 'py',
        projectName: projectName$.value,
        projectKey: projectKey ?? undefined,
      },
      fileObjects,
    );
  }

  function resetProject(projectLoadResult: ProjectLoadResult<ProjectFile[]>) {
    const { projectName, data: projectFiles, project } = projectLoadResult;
    files$.next(projectFiles);
    projectName$.next(projectName);
    projectKey$.next(project ?? null);

    // sketch.js default selection
    const mainFile = projectFiles.find((it) => it.name === 'main.py');
    if (mainFile) {
      selectedFile$.next(mainFile);
    }
  }

  // 기본 파일로 업데이트
  async function resetToNewProject() {
    try {
      projectLoading$.next(true);
      const result = await newProjectLoader.load();
      resetProject(result);
    } catch (err) {
      toast.warn('기본 프로젝트 로드 실패:' + errmsg(err));
    } finally {
      projectLoading$.next(false);
    }
  }

  const runCode = async (pythonCode: string) => {
    if (!pythonCode) return;
    const iframe = execIframe$.value;
    if (!iframe) {
      console.log('runCode(): iframe not ready');
      return;
    }

    console.log('runCode(): ', pythonCode);
    try {
      await iframe.runCmd('runPython', [pythonCode]);
    } catch (err) {
      console.warn(errmsg(err));
    }
  };

  const runCurrentCode = async () => {
    // 현재 선택된 파일을 실행한다
    const pythonCode = selectedFile$.value?.content;
    if (!pythonCode) return;
    await runCode(pythonCode);
  };

  // iframe to model: iframe 에디터의 내용 변경 감시
  const observeEditorTextChanged = (editor: IframeMonacoEditorType, currentFile: ProjectFile) => {
    return new Observable<string>((observer) => {
      const onTextChanged = (text: string) => {
        if (currentFile.content !== text) {
          currentFile.content = text;
          observer.next(text);
        }
      };
      editor.iframeController.events.on('text-changed', onTextChanged);
      return () => {
        editor.iframeController.events.off('text-changed', onTextChanged);
      };
    });
  };

  closables.addRx(
    // iframe to model: iframe 에디터의 내용 변경 감시
    combineLatest([selectedFile$, editor$], (selectedFile, editor) => {
      return editor && selectedFile ? { selectedFile, editor } : null;
    })
      .pipe(
        switchMap((it) => {
          if (!it) return of(null);
          const { editor, selectedFile } = it;
          return observeEditorTextChanged(editor, selectedFile);
        }),
        distinctUntilChanged(),
        tap(() => {
          fileChangedTimestamp$.next((fileChangedTimestamp$.value + 1) % Number.MAX_SAFE_INTEGER);
        }),
      )
      .subscribe(),

    // model to iframe: 선택된 파일이 변경되면 에디터에 표시한다
    combineLatest([selectedFile$, editor$], (selectedFile, editor) => {
      return editor ? { selectedFile, editor } : null;
    })
      .pipe(filter(Boolean))
      .subscribe(({ selectedFile, editor }) => {
        if (!selectedFile) {
          editor.setText('');
        } else {
          editor.updateLang(selectedFile.language);
          editor.setText(selectedFile.content);
        }
      }),

    // 변경이 생기면 실행
    combineLatest(
      [
        // html file이 존재하는지 체크
        files$.pipe(
          map((files) => files.find((f) => f.name === 'main.py')),
          distinctUntilChanged(),
        ),
        // iframe 존재여부 체크
        execIframe$,

        // 자동실행 여부
        autoRefreshEnabled$,

        // 파일의 내용 변경 체크
        fileChangedTimestamp$,
      ],
      (mainFileExists, iframe, autoRunEnabled, _fileChangeTime) => {
        return !!mainFileExists && !!iframe && autoRunEnabled;
      },
    )
      .pipe(filter(Boolean), debounceTime(1000))
      .subscribe(() => {
        runCurrentCode();
      }),

    // 파일 목록이 변경되면, selectedFile을 변경한다
    files$.subscribe((files) => {
      if (files.length === 0) {
        selectedFile$.next(null);
      } else {
        if (!selectedFile$.value) {
          selectedFile$.next(files[0]!);
          return;
        }

        if (!files.some((it) => it === selectedFile$.value)) {
          selectedFile$.next(files[0]!);
          return;
        }
      }
    }),

    // combineLatest(
    //   [
    //     execIframeDom$,
    //     fileContent$,

    //     // 자동실행 여부
    //     autoRefreshEnabled$,
    //   ],
    //   (iframe, fileContent, autoRefreshEnabled) => {
    //     if (!iframe || !autoRefreshEnabled || !fileContent) return null;
    //     return fileContent;
    //   },
    // )
    //   .pipe(filter(Boolean), debounceTime(1000))
    //   .subscribe((fileContent) => {
    //     runCode(fileContent);
    //   }),
  );

  // 실행중지는 어떻게 할 것인가?
  // 그냥 iframe remount 하는게 나을 듯
  const stopExecuting = () => {
    if (execIframe$.value) {
      execIframe$.value.stop();
    }
  };

  if (initialProjectLoader) {
    (async () => {
      try {
        projectLoading$.next(true);
        const result = await initialProjectLoader.load();
        resetProject(result);
      } catch (err) {
        toast.warn('프로젝트 로드 실패:' + errmsg(err));
      } finally {
        projectLoading$.next(false);
      }
    })();
  } else {
    if (!previewMode) {
      // 최초 기본 파일 생성
      resetToNewProject();
    }
  }

  function destroy() {
    closables.close();
  }

  const result = {
    execIframe$: asReadonlyBS(execIframe$),
    setExecIframe: createSetterBS(execIframe$),

    editor$: asReadonlyBS(editor$),
    files$: asReadonlyBS(files$),

    autoRefreshEnabled$: asReadonlyBS(autoRefreshEnabled$),
    setAutoRefreshEnabled: createSetterBS(autoRefreshEnabled$),

    projectLoading$: asReadonlyBS(projectLoading$),

    /**
     * 프로젝트 키
     * 서버에 저장된 프로젝트가 아닌 경우 null
     * 다른 소유자의 프로젝트일 수 있음
     */
    setProjectKey: createSetterBS(projectKey$),
    projectKey$: asReadonlyBS(projectKey$),

    /**
     * 프로젝트 이름
     */
    setProjectName: createSetterBS(projectName$),
    projectName$: asReadonlyBS(projectName$),

    serializeToPjtFile,

    setupCodeEditor,
    destroyCodeEditor,
    resetToNewProject,
    resetProject,

    runCurrentCode,
    stopExecuting,
    clearLogs,
    destroy,
  };

  if (ctx.canceled) {
    destroy();
  } else {
    console.log('atom app created');
    callback?.(result);
  }

  return result;
}
